import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  PrimaryButton,
  DefaultButton,
  makeStyles,
  Theme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Chip,
  Divider,
} from '@bb-ui/react-library';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '@bb-ui/react-library/dist/components/Checkbox';
import { SiteInfo, isEligibleForReminder } from '../../hooks/useCohortNotifications';

interface ReminderSitesDialogProps {
  open: boolean;
  onClose: () => void;
  onSendReminders: (selectedSiteIds: string[]) => void;
  sites: SiteInfo[];
  loading?: boolean;
}

const useStyles = makeStyles((theme: Theme) => ({
  dialogTitle: {
    paddingBottom: theme.spacing(1),
  },

  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    '& > *:not(:last-child)': {
      marginRight: theme.spacing(1),
    },
  },

  contentDescription: {
    marginBottom: theme.spacing(3),
    color: theme.palette.text.secondary,
    lineHeight: 1.6,
  },

  tableContainer: {
    maxHeight: '500px',
    marginBottom: theme.spacing(2),
    border: `1px solid ${theme.palette.grey[200]}`,
    borderRadius: theme.shape.borderRadius,
    overflow: 'auto',
  },

  tableHeader: {
    backgroundColor: theme.palette.grey[100],
    '& .MuiTableCell-head': {
      fontWeight: 600,
      color: theme.palette.text.primary,
      borderBottom: `2px solid ${theme.palette.grey[300]}`,
    },
  },

  checkboxCell: {
    width: 50,
    minWidth: 50,
    maxWidth: 50,
    padding: `${theme.spacing(1)} ${theme.spacing(1)}`,
    textAlign: 'center',
  },

  checkboxCellHeader: {
    width: 50,
    minWidth: 50,
    maxWidth: 50,
    padding: `${theme.spacing(1)} ${theme.spacing(1)}`,
    textAlign: 'center',
    fontWeight: 600,
    color: theme.palette.text.primary,
    borderBottom: `2px solid ${theme.palette.grey[300]}`,
  },

  siteIdCell: {
    width: 180,
    minWidth: 180,
    padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
  },

  siteIdCellHeader: {
    width: 180,
    minWidth: 180,
    padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
    fontWeight: 600,
    color: theme.palette.text.primary,
    borderBottom: `2px solid ${theme.palette.grey[300]}`,
  },

  urlCell: {
    width: 200,
    minWidth: 200,
    padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
  },

  urlCellHeader: {
    width: 200,
    minWidth: 200,
    padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
    fontWeight: 600,
    color: theme.palette.text.primary,
    borderBottom: `2px solid ${theme.palette.grey[300]}`,
  },

  statusCell: {
    width: 140,
    minWidth: 140,
    padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
    textAlign: 'center',
  },

  statusCellHeader: {
    width: 140,
    minWidth: 140,
    padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
    textAlign: 'center',
    fontWeight: 600,
    color: theme.palette.text.primary,
    borderBottom: `2px solid ${theme.palette.grey[300]}`,
  },

  tableRow: {
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    '&:hover': {
      backgroundColor: theme.palette.grey[100],
    },
    '&:not(:last-child)': {
      borderBottom: `1px solid ${theme.palette.grey[200]}`,
    },
  },

  selectedRow: {
    backgroundColor: `${theme.palette.primary.light}10`,
    '&:hover': {
      backgroundColor: `${theme.palette.primary.light}20`,
    },
  },

  disabledRow: {
    backgroundColor: theme.palette.grey[50],
    cursor: 'not-allowed',
    '&:hover': {
      backgroundColor: theme.palette.grey[50],
    },
  },

  cellContent: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  checkboxOnly: {
    padding: theme.spacing(0.5),
    '& .MuiSvgIcon-root': {
      fontSize: '1.2rem',
    },
  },

  sentChip: {
    backgroundColor: theme.palette.grade.critical,
    //color: theme.palette.info.contrastText,
    fontWeight: 500,
    fontSize: '0.75rem',
  },

  pendingChip: {
    backgroundColor: theme.palette.warning.main,
    color: theme.palette.warning.contrastText,
    fontWeight: 500,
    fontSize: '0.75rem',
  },

  acceptedChip: {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.success.contrastText,
    fontWeight: 500,
    fontSize: '0.75rem',
  },

  declinedChip: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    fontWeight: 500,
    fontSize: '0.75rem',
  },

  selectionSummary: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
    border: `1px solid ${theme.palette.grey[200]}`,
  },

  selectionCount: {
    fontWeight: 600,
    color: theme.palette.primary.main,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },

  dialogActions: {
    padding: theme.spacing(2, 3),
    backgroundColor: theme.palette.grey[50],
    borderTop: `1px solid ${theme.palette.grey[200]}`,
    gap: theme.spacing(2),
  },

  sendButton: {
    minWidth: 180,
    fontWeight: 600,
  },
}));

const ReminderSitesDialog: React.FC<ReminderSitesDialogProps> = ({
  open,
  onClose,
  onSendReminders,
  sites,
  loading = false,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [selectedSiteIds, setSelectedSiteIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (open) {
      setSelectedSiteIds(new Set());
    }
  }, [open]);

  const availableSites = sites.filter(isEligibleForReminder);
  const unavailableSites = sites.filter((site) => !isEligibleForReminder(site));

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedSiteIds(new Set(availableSites.map((site) => site.captain_id)));
    } else {
      setSelectedSiteIds(new Set());
    }
  };

  const handleSiteToggle = (siteId: string) => {
    const site = sites.find((s) => s.captain_id === siteId);
    if (!site || !isEligibleForReminder(site) || loading) return;

    setSelectedSiteIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(siteId)) {
        newSet.delete(siteId);
      } else {
        newSet.add(siteId);
      }
      return newSet;
    });
  };

  const handleRowClick = (siteId: string, event: React.MouseEvent) => {
    const target = event.target as HTMLElement;
    if (target.closest('.MuiCheckbox-root') || target.closest('[class*="checkboxCell"]')) {
      return;
    }

    if (!loading) {
      handleSiteToggle(siteId);
    }
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>, siteId: string) => {
    e.stopPropagation();
    if (!loading) {
      handleSiteToggle(siteId);
    }
  };

  const handleSendReminders = () => {
    if (!loading && selectedSiteIds.size > 0) {
      onSendReminders(Array.from(selectedSiteIds));
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  const renderStatusChip = (site: SiteInfo) => {
    if (site.site_status === 'ACCEPTED') {
      return (
        <Chip
          label={t('cohorts.reminderDialog.status.accepted', 'Accepted')}
          size="small"
          className={classes.acceptedChip}
        />
      );
    }
    if (site.site_status === 'DECLINED') {
      return (
        <Chip
          label={t('cohorts.reminderDialog.status.declined', 'Declined')}
          size="small"
          className={classes.declinedChip}
        />
      );
    }

    if (site.notifications?.resend_number && site.notifications.resend_number > 0) {
      return <Chip label="Reminder Sent" size="small" className={classes.sentChip} />;
    }

    if (isEligibleForReminder(site)) {
      return null;
    }

    return <Chip label={site.site_status || '-'} size="small" disabled />;
  };

  const selectedCount = selectedSiteIds.size;
  const availableSitesCount = availableSites.length;
  const allAvailableSelected = availableSitesCount > 0 && selectedCount === availableSitesCount;
  const someSelected = selectedCount > 0 && selectedCount < availableSitesCount;
  const hasSelection = selectedCount > 0;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        style: { minHeight: '600px', maxHeight: '90vh' },
      }}
    >
      <DialogTitle
        onClose={handleClose}
        className={classes.dialogTitle}
        closeLabel={t('global.close')}
      >
        <Box display="flex" alignItems="center" className={classes.titleContainer}>
          {t('cohorts.reminderDialog.title')}
        </Box>
      </DialogTitle>

      <DialogContent>
        <Typography className={classes.contentDescription}>
          {t('cohorts.reminderDialog.description')}
          {sites.length > 8 && (
            <Box
              component="span"
              sx={{ display: 'block', mt: 1, fontStyle: 'italic', fontSize: '0.9em' }}
            >
              {t('cohorts.reminderDialog.scrollHint', { count: sites.length })}
            </Box>
          )}
        </Typography>

        <TableContainer className={classes.tableContainer}>
          <Table stickyHeader>
            <TableHead className={classes.tableHeader}>
              <TableRow>
                <TableCell className={classes.checkboxCellHeader}>
                  <Checkbox
                    checked={allAvailableSelected}
                    indeterminate={someSelected}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className={classes.checkboxOnly}
                    disabled={availableSitesCount === 0 || loading}
                  />
                </TableCell>
                <TableCell className={classes.siteIdCellHeader}>
                  {t('cohorts.reminderDialog.tableHeaders.siteId')}
                </TableCell>
                <TableCell className={classes.urlCellHeader}>
                  {t('cohorts.reminderDialog.tableHeaders.learnUrl')}
                </TableCell>
                <TableCell className={classes.statusCellHeader}>
                  {t('cohorts.reminderDialog.tableHeaders.status')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sites.map((site) => {
                const isEligible = isEligibleForReminder(site);
                const isSelected = selectedSiteIds.has(site.captain_id);
                const isDisabled = !isEligible || loading;

                const rowClassName = `${classes.tableRow} ${
                  isSelected ? classes.selectedRow : ''
                } ${isDisabled ? classes.disabledRow : ''}`;

                return (
                  <TableRow
                    key={site.captain_id}
                    className={rowClassName}
                    onClick={(e) => !isDisabled && handleRowClick(site.captain_id, e)}
                  >
                    <TableCell
                      className={classes.checkboxCell}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Checkbox
                        checked={isSelected}
                        onChange={(e) => handleCheckboxChange(e, site.captain_id)}
                        className={classes.checkboxOnly}
                        disabled={isDisabled}
                      />
                    </TableCell>
                    <TableCell className={classes.siteIdCell}>
                      <Box className={classes.cellContent}>{site.captain_id}</Box>
                    </TableCell>
                    <TableCell className={classes.urlCell}>
                      <Box className={classes.cellContent}>{site.learn_url || '-'}</Box>
                    </TableCell>
                    <TableCell className={classes.statusCell}>{renderStatusChip(site)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <div className={classes.selectionSummary}>
          <Typography className={classes.selectionCount}>
            <span>{t('cohorts.reminderDialog.selectionSummary.sitesSelected')}</span>
            <Chip label={selectedCount} size="small" />
            <span>
              {t('cohorts.reminderDialog.selectionSummary.of')} {availableSitesCount}{' '}
              {t('cohorts.reminderDialog.selectionSummary.available')} ({unavailableSites.length}{' '}
              {t('cohorts.reminderDialog.selectionSummary.alreadySent')})
            </span>
          </Typography>
        </div>
      </DialogContent>

      <Divider />

      <DialogActions className={classes.dialogActions}>
        <DefaultButton onClick={handleClose} size="large" disabled={loading}>
          {t('cohorts.reminderDialog.buttons.cancel')}
        </DefaultButton>
        <PrimaryButton
          onClick={handleSendReminders}
          disabled={!hasSelection || loading}
          className={classes.sendButton}
          size="large"
        >
          {loading
            ? t('cohorts.reminderDialog.buttons.sending')
            : `${t('cohorts.reminderDialog.buttons.sendReminders')} (${selectedCount})`}
        </PrimaryButton>
      </DialogActions>
    </Dialog>
  );
};

export default ReminderSitesDialog;
